/**
 * Single source of truth for rendering dates in the UI.
 *
 * Every screen that displays a date (tables, cards, dialogs, badges) MUST
 * format it through one of the helpers below instead of calling
 * `toLocaleDateString` / `Intl.DateTimeFormat` / `date-fns` `format()`
 * directly. That keeps every rendered date consistent across the app and
 * gives us one place to change the format later.
 *
 * Locale is currently a hardcoded app-wide constant (`APP_LOCALE`) rather
 * than a user preference — the whole app renders Polish today (translations live in
 * `packages/screens/src/locales/`). `APP_LOCALE` and `getDateFnsLocale()` are the two things to change to turn this into a
 * per-user setting later: read the locale from user context instead of the
 * constant, and pick the matching `date-fns/locale` entry.
 */
import { format } from 'date-fns';
import { pl, enUS } from 'date-fns/locale';
import type { Locale } from 'date-fns';

/** App-wide display locale. Swap this for a user-setting lookup later. */
export const APP_LOCALE = 'pl' as const;

/** date-fns locale matching APP_LOCALE. Extend with a lookup map if more locales are added. */
export function getDateFnsLocale(): Locale {
  return pl;
}

/** i18next language code -> date-fns locale, for the handful of callers (see
 * `formatShortMonth` below) that need to track the live `i18n.language` instead of the
 * app-wide `APP_LOCALE` constant. */
const DATE_FNS_LOCALES_BY_LANGUAGE: Record<string, Locale> = { pl, en: enUS };

function resolveDateFnsLocale(language: string): Locale {
  return DATE_FNS_LOCALES_BY_LANGUAGE[language] ?? pl;
}

/** Canonical date-only display format: e.g. "1 sie 2026". */
const DATE_FORMAT = 'd MMM yyyy';
/** Compact date-only display format (no year), for tight ranges: e.g. "1 sie". */
const DATE_FORMAT_SHORT = 'd MMM';
/** Canonical date+time display format: e.g. "1 sie 2026, 14:30". */
const DATE_TIME_FORMAT = 'd MMM yyyy, HH:mm';
/** En dash with surrounding spaces, used to join a formatted date range. */
const DATE_RANGE_SEPARATOR = ' \u2013 ';

/** Matches a bare calendar-date string with no time/zone component, e.g. "2026-08-01". */
const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

function toDate(value: Date | string): Date {
  if (value instanceof Date) return value;
  if (DATE_ONLY_PATTERN.test(value)) {
    // A bare "YYYY-MM-DD" string (e.g. reservation dateFrom/dateTo) carries no
    // time/zone information. `new Date(str)` parses it as UTC midnight per the
    // ECMA-262 spec, but `date-fns` `format()` below reads the Date's local
    // wall-clock components — round-tripping through UTC would roll the
    // displayed calendar day back by one in any negative-UTC-offset timezone
    // (e.g. "2026-06-01" would render as "31 maj 2026" in America/Los_Angeles).
    // Parse the Y/M/D components directly into a local-midnight Date instead,
    // so the displayed calendar date never depends on the viewer's timezone.
    const parts = value.split('-').map(Number);
    const year = parts[0] ?? 0;
    const month = parts[1] ?? 1;
    const day = parts[2] ?? 1;
    const result = new Date(year, month - 1, day);
    // `new Date(y, m, d)` silently normalizes out-of-range components (e.g.
    // month 13, day 45) into a different, valid-looking rolled-over date
    // instead of producing an invalid Date. Reject anything that didn't
    // round-trip so malformed input still falls through to the documented
    // "" fallback in the formatters below, instead of rendering a wrong date.
    if (
      result.getFullYear() !== year ||
      result.getMonth() !== month - 1 ||
      result.getDate() !== day
    ) {
      return new Date(NaN);
    }
    return result;
  }
  return new Date(value);
}

/**
 * Formats a Date (or ISO/date string) as a short display date, e.g. "1 sie 2026".
 * Returns an empty string for an invalid date instead of throwing or
 * rendering "Invalid Date".
 */
export function formatDisplayDate(value: Date | string): string {
  const date = toDate(value);
  if (Number.isNaN(date.getTime())) return '';
  return format(date, DATE_FORMAT, { locale: getDateFnsLocale() });
}

/**
 * Formats a Date (or ISO/date string) as a compact display date with no year,
 * e.g. "1 sie". Use only for tight spaces (e.g. a min–max range summary)
 * where the year is either implied or shown elsewhere.
 * Returns an empty string for an invalid date instead of throwing or
 * rendering "Invalid Date".
 */
export function formatDisplayDateShort(value: Date | string): string {
  const date = toDate(value);
  if (Number.isNaN(date.getTime())) return '';
  return format(date, DATE_FORMAT_SHORT, { locale: getDateFnsLocale() });
}

/**
 * Formats a Date (or ISO/date string) as a display date+time, e.g. "1 sie 2026, 14:30".
 * Returns an empty string for an invalid date instead of throwing or
 * rendering "Invalid Date".
 */
export function formatDisplayDateTime(value: Date | string): string {
  const date = toDate(value);
  if (Number.isNaN(date.getTime())) return '';
  return format(date, DATE_TIME_FORMAT, { locale: getDateFnsLocale() });
}

/**
 * Formats a [from, to] pair as a single display range, e.g.
 * "1 sie 2026 – 31 sie 2026". Use this instead of concatenating two
 * `formatDisplayDate()` calls by hand so the separator stays consistent.
 */
export function formatDisplayDateRange(from: Date | string, to: Date | string): string {
  return `${formatDisplayDate(from)}${DATE_RANGE_SEPARATOR}${formatDisplayDate(to)}`;
}

/** date-fns format token for a locale-aware abbreviated month name, e.g. "kwi" / "Apr". */
const SHORT_MONTH_FORMAT = 'MMM';

/**
 * Formats a single calendar date's locale-aware short month abbreviation only, e.g.
 * "kwi" (pl) / "Apr" (en). Used by the client-offer period-axis chip label
 * (`formatPeriodLabel` in
 * `src/features/offers/client-offer/lib/client-offer-availability-helpers.ts`) to build a
 * compact "01–15 kwi" range with the month shown once, rather than the full
 * `formatDisplayDateShort` range.
 *
 * Unlike every other helper in this module (hardcoded to `APP_LOCALE` — see the module
 * docstring), this one accepts an explicit `language` because its only caller already
 * tracks the live `i18n.language` per-hook: the app ships both `en` and `pl` locale
 * files, so a hardcoded Polish formatter there would render Polish month names even
 * when the rest of the screen is displaying English copy. Falls back to `APP_LOCALE`
 * for an unrecognized language.
 */
export function formatShortMonth(value: Date | string, language: string = APP_LOCALE): string {
  const date = toDate(value);
  if (Number.isNaN(date.getTime())) return '';
  return format(date, SHORT_MONTH_FORMAT, { locale: resolveDateFnsLocale(language) });
}
